import { useState, useEffect } from "react";
import { Flame, ShoppingBag, Truck, MapPin } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const DELIVERY_BUTTON_IDS = {
  "autumn-candle": process.env.REACT_APP_STRIPE_AUTUMN_BTN,
  "blue-agave-candle": process.env.REACT_APP_STRIPE_BLUE_AGAVE_BTN,
};

const ProductCard = ({ product, reverse }) => {
  const [loading, setLoading] = useState(false);
  const [fulfillment, setFulfillment] = useState("pickup");

  const price = fulfillment === "delivery" ? product.price_delivery : product.price_pickup;

  const handlePickupBuy = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/products/checkout`, {
        product_id: product.id,
        origin_url: originUrl,
        fulfillment: "pickup",
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error("Checkout error:", e);
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`product-${product.id}`}
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center"
    >
      {/* Image */}
      <div className={`${reverse ? "lg:order-2" : ""}`}>
        <div className="relative overflow-hidden rounded-lg bg-[#F9F6F0]">
          <img
            data-testid={`product-image-${product.id}`}
            src={product.image}
            alt={product.name}
            className="w-full h-[400px] md:h-[500px] object-contain transition-transform duration-700 hover:scale-105"
          />
        </div>
      </div>

      {/* Details */}
      <div className={`${reverse ? "lg:order-1" : ""}`}>
        <div className="flex items-center gap-2 mb-4">
          <Flame size={18} className="text-[#CC5543]" />
          <span className="text-xs uppercase tracking-[0.2em] text-[#596B5B] font-medium">
            {product.category}
          </span>
        </div>
        <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#1A3626] tracking-tight mb-4">
          {product.name}
        </h3>
        <p className="text-sm text-[#596B5B] leading-relaxed mb-6">
          {product.description}
        </p>

        {/* Pickup / Delivery Toggle */}
        <div className="flex gap-3 mb-6">
          <button
            data-testid={`pickup-btn-${product.id}`}
            onClick={() => setFulfillment("pickup")}
            className={`flex items-center gap-2 px-5 py-3 rounded-full text-xs uppercase tracking-[0.15em] font-medium border transition-all duration-300 ${
              fulfillment === "pickup"
                ? "bg-[#1A3626] text-white border-[#1A3626]"
                : "bg-transparent text-[#596B5B] border-[#DCD6CC] hover:border-[#1A3626] hover:text-[#1A3626]"
            }`}
          >
            <MapPin size={14} />
            Pickup — ${product.price_pickup.toFixed(0)}
          </button>
          <button
            data-testid={`delivery-btn-${product.id}`}
            onClick={() => setFulfillment("delivery")}
            className={`flex items-center gap-2 px-5 py-3 rounded-full text-xs uppercase tracking-[0.15em] font-medium border transition-all duration-300 ${
              fulfillment === "delivery"
                ? "bg-[#1A3626] text-white border-[#1A3626]"
                : "bg-transparent text-[#596B5B] border-[#DCD6CC] hover:border-[#1A3626] hover:text-[#1A3626]"
            }`}
          >
            <Truck size={14} />
            Delivery — ${product.price_delivery.toFixed(0)}
          </button>
        </div>

        <p className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A3626] mb-8">
          ${price.toFixed(2)}
        </p>

        {/* Pickup: custom styled button */}
        {fulfillment === "pickup" && (
          <button
            data-testid={`buy-btn-${product.id}`}
            onClick={handlePickupBuy}
            disabled={loading}
            className="inline-flex items-center gap-3 px-10 py-4 bg-[#CC5543] text-white text-xs uppercase tracking-[0.2em] font-semibold rounded-full hover:bg-[#A94334] transition-all duration-300 hover:shadow-lg hover:shadow-[#CC5543]/20 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <ShoppingBag size={16} />
            {loading ? "Redirecting..." : "Buy Now — Pickup"}
          </button>
        )}

        {/* Delivery: Stripe Buy Button (collects shipping address) */}
        {fulfillment === "delivery" && (
          <div data-testid={`delivery-buy-${product.id}`}>
            <stripe-buy-button
              buy-button-id={DELIVERY_BUTTON_IDS[product.id]}
              publishable-key={STRIPE_PK}
            >
            </stripe-buy-button>
          </div>
        )}
      </div>
    </div>
  );
};

const ProductsSection = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (e) {
        console.error("Error fetching products:", e);
      }
    };
    fetchProducts();
  }, []);

  if (products.length === 0) return null;

  return (
    <section data-testid="products-section" id="products" className="py-24 md:py-32 bg-[#EBE6DF]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#596B5B] mb-3 font-medium">
            Shop
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-[#1A3626] tracking-tight">
            Hand-Poured Candles
          </h2>
          <p className="mt-4 text-sm text-[#596B5B] max-w-lg leading-relaxed">
            Each candle is crafted with intention, designed to create an atmosphere of calm and connection.
          </p>
        </div>

        <div className="space-y-24">
          {products.map((product, i) => (
            <ProductCard key={product.id} product={product} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
